'use client';
import AuthGuard from '@/components/complex/auth-guard';
import { useEffect, useState } from 'react';
import { useHeader } from '@/components/complex/header';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import api from '@/api';
import { useFeedForm } from './hooks/useFeedForm';
import { useFeedUpload } from './hooks/useFeedUpload';
import { FeedLinkDto } from '@/api/feed';
import FeedWriteStep2 from '@/screen/feed/write/feed-write-step2';
import FeedWriteStep1 from '@/screen/feed/write/feed-write-step1';
import styles from './styles.module.scss';
import { usePreventPageLeave } from '@/hooks/use-prevent-page-leave';
import { useAuth } from '@/context/auth-context';

interface FeedWriteScreenProps {
  feedId?: string;
  placeId?: string;
}

/**
 * Feed writing screen component
 */
const FeedWriteScreen = ({ feedId, placeId }: FeedWriteScreenProps) => {
  // Step state
  const [step, setStep] = useState<1 | 2>(1); // 1: Photo registration, 2: Caption and link
  const [bottomFixedViewHeight, setBottomFixedViewHeight] = useState(0);
  const [initialPhotos, setInitialPhotos] = useState<any[]>([]);
  const [initialCaption, setInitialCaption] = useState('');
  const [initialLinks, setInitialLinks] = useState<FeedLinkDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();

  // Fetch feeds data if feedId is provided
  useEffect(() => {
    const fetchFeed = async () => {
      if (feedId) {
        try {
          setIsLoading(true);
          const feed = await api.feed.get(feedId);

          // Convert feeds files to PhotoPickerFile format
          const photos = feed.files.map(file => ({
            id: file.id,
            sortOrder: file.sortOrder,
            isUploaded: true,
            file: file.file,
            localFile: null,
            links: file.links
          }));

          setInitialPhotos(photos);
          setInitialCaption(feed.caption);
          setInitialLinks(feed.links);
        } catch (error) {
          console.error('Error fetching feeds:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchFeed().then();
  }, [feedId]);

  // If query has placeId, fetch place and append to initialLinks
  useEffect(() => {
    const applyPlaceFromQuery = async () => {
      if (!placeId) return;
      const placeIdNum = Number(placeId);
      if (!Number.isFinite(placeIdNum) || placeIdNum <= 0) return;
      try {
        setIsLoading(true);
        const place = await api.place.get(placeIdNum);
        setInitialLinks(prev => {
          const exists = prev.some(l => l.place?.id === place.id);
          if (exists) return prev;
          const nextOrder = prev.length;
          const newLink: FeedLinkDto = { place, sortOrder: nextOrder };
          return [...prev, newLink];
        });
      } catch (e) {
        console.error('Error fetching place by placeId param:', e);
      } finally {
        setIsLoading(false);
      }
    };

    applyPlaceFromQuery().then();
  }, [placeId]);

  // Use custom hooks
  const { 
    photos, 
    setPhotos, 
    caption, 
    setCaption, 
    links, 
    setLinks, 
    isDirty, 
    setIsDirty 
  } = useFeedForm({
    initialPhotos,
    initialCaption,
    initialLinks
  });


  const { 
    isLoading: isUploading, 
    uploadProgress,
    currentFileIndex,
    totalFiles,
    isFeedRegistering,
    uploadFeed 
  } = useFeedUpload({
    feedId,
    onUploadComplete: () => setIsDirty(false),
    onUploadError: () => setIsDirty(true)
  });

  // 로그인 한 상태일 때만 나가기 방지
  usePreventPageLeave({ isDirty: isDirty && !!auth.me?.activeAccount })

  // Set up header
  useHeader({
    title: feedId ? '피드 수정' : '피드 작성',
    useBack: true,
  });

  // Disable bottom navigation
  useBottomNavigation(false);

  // Handle next step
  const handleNext = async () => {
    if (step === 1) {
      if (photos.length > 0) {
        setStep(2);
      }
    } else {
      // 저장 시작 전에 isDirty를 false로 설정하여 페이지 나가기 경고 방지
      setIsDirty(false);
      await uploadFeed(photos, caption, links);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  return (
    <AuthGuard>
      <div 
        className={styles.container} 
        style={{paddingBottom: bottomFixedViewHeight}}
      >
        {step === 1 ? (
          <FeedWriteStep1 
            photos={photos} 
            setPhotos={setPhotos} 
            handleNext={handleNext} 
            setBottomFixedViewHeight={setBottomFixedViewHeight} 
          />
        ) : (
          <FeedWriteStep2
            feedId={feedId}
            caption={caption} 
            setCaption={setCaption} 
            links={links} 
            setLinks={setLinks} 
            handleNext={handleNext} 
            handlePrevious={handlePrevious}
            setBottomFixedViewHeight={setBottomFixedViewHeight}
            isLoading={isLoading || isUploading}
            uploadProgress={uploadProgress}
            currentFileIndex={currentFileIndex}
            totalFiles={totalFiles}
            isFeedRegistering={isFeedRegistering}
          />
        )}
      </div>
    </AuthGuard>
  );
};

export default FeedWriteScreen;
